import { useState, useEffect } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Separator } from "@/components/ui/separator";
import { CalendarDays, Package, CreditCard, Clock, BookOpen } from "lucide-react";
import { cn } from "@/lib/utils";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";
import { useLocalization } from "@/hooks/useLocalization";
import { OrderCarousel } from "@/components/customer/OrderCarousel";
import { useSearchParams } from "react-router-dom";
import { useCustomerAuth } from "@/components/customer/CustomerAuthProvider";
import { PaymentRequestModal } from "@/components/customer/PaymentRequestModal";
import { ErrorBoundary } from "@/components/customer/ErrorBoundary";
import { PaymentProgress } from "@/components/customer/PaymentProgress";
import { formatPeopleDate, formatDateWithA11y, formatPaymentTime } from "@/lib/dateUtils";

// Data Types
interface OrderData {
  order: {
    id: string;
    order_id: string;
    status: string;
    eta: string;
    total: number;
    deposit: number;
    delivery_date?: string;
    first_fitting_date?: string;
    second_fitting_date?: string;
    product?: {
      name: string;
      thumb: string;
      size: string;
      color: string;
      brand?: string;
    };
  };
  invoices: Array<{
    id: string;
    invoice_number: string;
    amount: number;
    status: string;
    issued: string;
    due_date?: string;
  }>;
  payments: Array<{
    id: string;
    amount: number;
    method: string;
    date: string;
    paid_at?: string;
    is_stripe_payment?: boolean;
    invoice_id: string;
  }>;
  payment_requests: Array<{
    id: string;
    amount: number;
    due_date: string;
    status: string;
    description: string;
    created_at: string;
    invoice_id: string;
    remaining_cents: number;
    paid_amount: number;
  }>;
  timeline: Array<{
    ts: string;
    event: string;
    description: string;
  }>;
}

interface OrderSummary {
  id: string;
  order_id: string;
  thumb: string;
  status: string;
  unpaid: number;
  updated: string;
  created: string;
  isActive: boolean;
  product?: {
    name: string;
    color: string;
    brand: string;
  };
  eta?: string;
}

interface OrderListResponse {
  orders: OrderSummary[];
  summary: {
    active: number;
    completed: number;
    totalUnpaid: number;
  };
}

// Status Bar Component
interface StatusStepProps {
  step: string;
  isActive: boolean;
  isCompleted: boolean;
  eta?: string;
}

function StatusStep({ step, isActive, isCompleted, eta }: StatusStepProps) {
  return (
    <div className="flex flex-col items-center text-center">
      <div className={cn(
        "w-8 h-8 rounded-full flex items-center justify-center text-sm font-medium mb-2 transition-colors",
        isCompleted ? "bg-green-500 text-white" :
        isActive ? "bg-blue-500 text-white" :
        "bg-gray-200 text-gray-500"
      )}>
        {isCompleted ? "✓" : step.charAt(0)}
      </div>
      <div className={cn(
        "text-xs font-medium mb-1",
        isActive ? "text-blue-600" : "text-gray-600"
      )}>
        {step}
      </div>
      {isActive && eta && (
        <div className="text-xs text-gray-500">
          <time 
            dateTime={formatDateWithA11y(eta).ariaLabel}
            title={formatDateWithA11y(eta).ariaLabel}
          >
            {formatPeopleDate(eta, { includeTime: true })}
          </time>
        </div>
      )}
    </div>
  );
}

interface StatusBarProps {
  status: string;
  eta: string;
}

function StatusBar({ status, eta }: StatusBarProps) {
  const { t } = useLocalization();
  const steps = [t('order'), t('production'), t('ready')];
  
  const getStepStatus = (stepName: string, currentStatus: string) => {
    const stepIndex = steps.indexOf(stepName);
    const currentIndex = status === "pending" ? 0 : 
                        status === "deposit" || status === "production" ? 1 : 2;
    
    return {
      isCompleted: stepIndex < currentIndex,
      isActive: stepIndex === currentIndex
    };
  };

  return (
    <div className="bg-card p-4 sm:p-6 rounded-lg border shadow-sm">
      <div className="flex items-center justify-between">
        {steps.map((step, index) => {
          const { isCompleted, isActive } = getStepStatus(step, status);
          return (
            <div key={step} className={cn(
              "flex items-center",
              index < steps.length - 1 ? "flex-1" : ""
            )}>
              <StatusStep 
                step={step} 
                isActive={isActive} 
                isCompleted={isCompleted}
                eta={isActive ? eta : undefined}
              />
              {index < steps.length - 1 && (
                <div className={cn(
                  "flex-1 h-0.5 transition-colors",
                  isCompleted ? "bg-success" : "bg-border"
                )} />
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}

// Filter Chips Component
interface FilterChipsProps {
  activeCount: number;
  completedCount: number;
  currentFilter: 'active' | 'completed' | 'all';
  onFilterChange: (filter: 'active' | 'completed' | 'all') => void;
}

function FilterChips({ activeCount, completedCount, currentFilter, onFilterChange }: FilterChipsProps) {
  const { t } = useLocalization();
  return (
    <div className="flex gap-2 mb-4">
      <Button
        variant={currentFilter === 'active' ? 'default' : 'outline'}
        size="sm"
        onClick={() => onFilterChange('active')}
        className="rounded-full"
      >
        {t('active')} ({activeCount})
      </Button>
      <Button
        variant={currentFilter === 'completed' ? 'default' : 'outline'}
        size="sm"
        onClick={() => onFilterChange('completed')}
        className="rounded-full"
      >
        {t('completed')} ({completedCount})
      </Button>
    </div>
  );
}

// Main Component
export default function CustomerOrders() {
  const [ordersList, setOrdersList] = useState<OrderSummary[]>([]);
  const [orderData, setOrderData] = useState<OrderData | null>(null);
  const [summary, setSummary] = useState({ active: 0, completed: 0, totalUnpaid: 0 });
  const [loading, setLoading] = useState(true);
  const [selectedOrderId, setSelectedOrderId] = useState<string>("");
  const [filter, setFilter] = useState<'active' | 'completed' | 'all'>('active');
  const [paymentRequestModalOpen, setPaymentRequestModalOpen] = useState(false);
  const [selectedPaymentRequest, setSelectedPaymentRequest] = useState<any>(null);
  const [stripeConnected, setStripeConnected] = useState<boolean>(false);
  const [orgMinPaymentCents, setOrgMinPaymentCents] = useState<number>(2000);
  const { toast } = useToast();
  const { t } = useLocalization();
  const [searchParams, setSearchParams] = useSearchParams();
  const { customer } = useCustomerAuth();

  // Check Stripe Connect status and load organization settings
  const loadOrganizationSettings = async () => {
    if (!customer?.organization_id) return;

    try {
      const { data, error } = await supabase.functions.invoke('customer-organization-settings', {
        body: { 
          organization_id: customer.organization_id,
          session_token: 'customer_portal_session'
        }
      });

      if (error) throw error;
      
      // Check if Stripe is connected
      const stripeConnected = !!data?.stripe_connected;
      setStripeConnected(stripeConnected);
      
      // Load minimum payment amount
      const minPaymentCents = data?.settings_data?.billing?.min_payment_amount_cents || 2000;
      setOrgMinPaymentCents(minPaymentCents);
      
      console.log('Organization settings loaded:', { stripeConnected, minPaymentCents });
      return { stripeConnected, minPaymentCents };
    } catch (error) {
      console.error('Error loading organization settings:', error);
      setStripeConnected(false);
      setOrgMinPaymentCents(2000); // Default fallback
      return { stripeConnected: false, minPaymentCents: 2000 };
    }
  };

  // Load orders list
  const fetchOrdersList = async () => {
    if (!customer?.email) {
      console.log('No customer email available');
      return;
    }

    try {
      setLoading(true);
      
      const { data: { session } } = await supabase.auth.getSession();

      const { data, error } = await supabase.functions.invoke('customer-orders-list', {
        body: { 
          customer_email: customer.email,
          session_token: session?.access_token || 'customer_portal_session'
        }
      });

      if (error) throw error;
      if (data.error) throw new Error(data.error);

      const response = data as OrderListResponse;
      setOrdersList(response.orders);
      setSummary(response.summary);

      // Handle deep linking
      const orderParam = searchParams.get('order');
      const requestParam = searchParams.get('requestId');
      
      if (orderParam) {
        const targetOrder = response.orders.find(o => o.id === orderParam);
        if (targetOrder) {
          setSelectedOrderId(orderParam);
          // Update filter to show the target order
          setFilter(targetOrder.isActive ? 'active' : 'completed');
          
          // Handle payment request deep linking
          if (requestParam) {
            // Store the request ID for later when order data loads
            setTimeout(() => {
              fetchOrderData(orderParam);
            }, 100);
          }
        }
      } else if (response.orders.length > 0) {
        // Auto-select first active order or most recent
        const activeOrder = response.orders.find(order => order.isActive);
        const targetOrder = activeOrder || response.orders[0];
        setSelectedOrderId(targetOrder.id);
        setFilter(targetOrder.isActive ? 'active' : 'completed');
      }

    } catch (error) {
      console.error('Error fetching orders list:', error);
      toast({
        title: t('error'),
        description: t('orders.customer.loadOrdersError'),
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  // Load specific order details
  const fetchOrderData = async (orderId: string) => {
    if (!customer?.email) return;

    try {
      const { data: { session } } = await supabase.auth.getSession();
      const selectedOrder = ordersList.find(o => o.id === orderId);

      // Fetch order details and payment requests in parallel
      const [orderDetailsResult, paymentRequestsResult] = await Promise.all([
        supabase.functions.invoke('customer-order-details', {
          body: { 
            customer_email: customer.email,
            session_token: session?.access_token || 'customer_portal_session',
            order_id: selectedOrder?.order_id
          }
        }),
        supabase.functions.invoke('portal-payment-requests', {
          body: {
            orderId: selectedOrder?.order_id,
            customerEmail: customer.email
          }
        })
      ]);

      if (orderDetailsResult.error) throw orderDetailsResult.error;
      if (orderDetailsResult.data.error) throw new Error(orderDetailsResult.data.error);

      if (paymentRequestsResult.error) {
        console.warn('Failed to load payment requests:', paymentRequestsResult.error);
      }

      // Merge payment requests data with order data
      const orderDataWithRequests = {
        ...orderDetailsResult.data,
        payment_requests: paymentRequestsResult.data?.payment_requests || []
      };

      setOrderData(orderDataWithRequests);
      // Handle payment request deep linking after data loads
      const requestParam = searchParams.get('requestId');
      if (requestParam && paymentRequestsResult.data?.payment_requests) {
        const request = paymentRequestsResult.data.payment_requests.find((r: any) => r.id === requestParam);
        if (request && request.status !== 'paid' && request.remaining_cents > 0 && stripeConnected) {
          // Clear the URL parameter first
          setSearchParams(prev => {
            const newParams = new URLSearchParams(prev);
            newParams.delete('requestId');
            return newParams;
          }, { replace: true });
          
          // Open payment modal after a short delay to ensure data is loaded
          setTimeout(() => {
            setSelectedPaymentRequest(request);
            setPaymentRequestModalOpen(true);
          }, 300);
        } else if (!request) {
          console.warn('Payment request not found for deep link:', requestParam);
          // Clear invalid requestId from URL
          setSearchParams(prev => {
            const newParams = new URLSearchParams(prev);
            newParams.delete('requestId');
            return newParams;
          }, { replace: true });
        }
      }
    } catch (error) {
      console.error('Error fetching order details:', error);
      toast({
        title: t('error'),
        description: t('orders.customer.loadOrderDetailsError'),
        variant: "destructive",
      });
    }
  };

  useEffect(() => {
    if (customer?.email) {
      // Load both in parallel to reduce delay
      Promise.all([
        fetchOrdersList(),
        loadOrganizationSettings()
      ]).catch((error) => {
        console.error('Failed to load initial data:', error);
      });
    }
  }, [customer]);

  useEffect(() => {
    if (selectedOrderId) {
      fetchOrderData(selectedOrderId);
      // Update URL without causing navigation
      setSearchParams({ order: selectedOrderId }, { replace: true });
    }
  }, [selectedOrderId, setSearchParams]);

  const handlePayNow = async (invoiceId: string) => {
    if (!customer?.email) return;

    try {
      const { data, error } = await supabase.functions.invoke('customer-create-payment', {
        body: {
          invoiceId,
          customerEmail: customer.email,
        },
      });

      if (error) throw error;
      if ((data as any)?.error) throw new Error((data as any).error);

      if ((data as any)?.url) {
        window.open((data as any).url, '_blank');
      } else {
        throw new Error(t('orders.customer.createPaymentError'));
      }
    } catch (err: any) {
      console.error('Error creating payment:', err);
      toast({
        title: t('error'),
        description: err?.message || t('orders.customer.createPaymentError'),
        variant: "destructive",
      });
    }
  };

  const handlePayPaymentRequest = (request: any) => {
    console.log('Opening payment modal for request:', request);
    console.log('Request invoice_number:', request.invoice_number);
    const selectedOrder = ordersList.find(o => o.id === selectedOrderId);
    if (!selectedOrder) return;

    const selectedRequest = {
      ...request,
      order_id: selectedOrder.order_id,
      // Explicitly preserve invoice_number and other important fields
      invoice_number: request.invoice_number,
      // Ensure we have the current remaining amount
      remaining_cents: request.remaining_cents || (request.amount * 100)
    };
    
    console.log('Selected request object:', selectedRequest);
    setSelectedPaymentRequest(selectedRequest);
    setPaymentRequestModalOpen(true);
  };

  // Empty state for no orders
  if (!loading && ordersList.length === 0) {
    return (
      <div className="container mx-auto p-4 max-w-4xl">
        <div className="text-center py-12">
          <div className="w-32 h-32 mx-auto mb-6 bg-gradient-to-br from-pink-100 to-purple-100 rounded-full flex items-center justify-center">
            <Package className="w-16 h-16 text-purple-500" />
          </div>
          <h1 className="text-2xl font-bold mb-4">{t('start_dress_journey')}</h1>
          <p className="text-muted-foreground mb-6 max-w-md mx-auto">
            {t('no_orders_message')}
          </p>
          <Button size="lg" className="rounded-full">
            <BookOpen className="w-4 h-4 mr-2" />
            {t('book_appointment')}
          </Button>
        </div>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="container mx-auto p-4 max-w-4xl">
        <div className="animate-pulse space-y-4">
          <div className="h-8 bg-gray-200 rounded w-1/4"></div>
          <div className="h-32 bg-gray-200 rounded"></div>
          <div className="h-64 bg-gray-200 rounded"></div>
        </div>
      </div>
    );
  }

  return (
    <ErrorBoundary>
      <div className="container mx-auto p-4 max-w-4xl space-y-6">
        {/* Header */}
        <div>
          <h1 className="text-2xl font-bold mb-2">{t('your_orders')}</h1>
          <p className="text-muted-foreground">
            {summary.totalUnpaid > 0 && (
              <Badge variant="destructive" className="mr-2">
                {summary.totalUnpaid} {summary.totalUnpaid === 1 ? t('unpaid_invoice') : t('unpaid_invoices')}
              </Badge>
            )}
            {ordersList.length} {ordersList.length === 1 ? t('order_total') : t('orders_total')}
          </p>
        </div>

        {/* Handle filter change and clear selection if no orders match */}
        {/* Filter Chips */}
        <FilterChips
          activeCount={summary.active}
          completedCount={summary.completed}
          currentFilter={filter}
          onFilterChange={(newFilter) => {
            setFilter(newFilter);
            
            // Filter orders based on new filter
            const filteredOrders = ordersList.filter(order => {
              if (newFilter === 'active') return order.isActive;
              if (newFilter === 'completed') return !order.isActive;
              return true;
            });
            
            // If no orders match the new filter, clear selection and order data
            if (filteredOrders.length === 0) {
              setSelectedOrderId("");
              setOrderData(null);
              setSearchParams({}, { replace: true });
            } else {
              // Check if current selected order is still valid in new filter
              const isCurrentOrderValid = filteredOrders.some(order => order.id === selectedOrderId);
              if (!isCurrentOrderValid) {
                // Auto-select first order in the new filter
                const activeOrder = filteredOrders.find(order => order.isActive);
                const targetOrder = activeOrder || filteredOrders[0];
                setSelectedOrderId(targetOrder.id);
              }
            }
          }}
        />

        {/* Order Carousel */}
        <OrderCarousel
          orders={ordersList}
          selectedOrderId={selectedOrderId}
          onOrderSelect={setSelectedOrderId}
          filter={filter}
        />

        {/* Order Details - Only show if orderData exists and selected order is in current filter */}
        {orderData && selectedOrderId && (() => {
          const filteredOrders = ordersList.filter(order => {
            if (filter === 'active') return order.isActive;
            if (filter === 'completed') return !order.isActive;
            return true;
          });
          return filteredOrders.some(order => order.id === selectedOrderId);
        })() && (
          <>
            {/* Status Bar */}
            <StatusBar status={orderData.order.status} eta={orderData.order.eta} />

            {/* Order Summary */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Package className="w-5 h-5" />
                  {t('order_details')}
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                {orderData.order.product && (
                  <div className="flex gap-4">
                    <img 
                      src={orderData.order.product.thumb || "https://images.unsplash.com/photo-1594736797933-d0d62ee3f03a?w=400&h=400&fit=crop&crop=center"} 
                      alt={orderData.order.product.name}
                      className="w-24 h-24 object-cover rounded-lg"
                      onError={(e) => {
                        e.currentTarget.src = "https://images.unsplash.com/photo-1594736797933-d0d62ee3f03a?w=400&h=400&fit=crop&crop=center";
                      }}
                    />
                    <div>
                      <h3 className="font-semibold">{orderData.order.product.name}</h3>
                      <p className="text-sm text-muted-foreground">{t('size')}: {orderData.order.product.size}</p>
                      <p className="text-sm text-muted-foreground">{t('color')}: {orderData.order.product.color}</p>
                      {orderData.order.product.brand && (
                        <p className="text-sm text-muted-foreground">{t('brand')}: {orderData.order.product.brand}</p>
                      )}
                    </div>
                  </div>
                )}
                
                <div className="grid grid-cols-2 gap-4 text-sm">
                  <div>
                    <span className="text-muted-foreground">{t('total_amount_colon')}</span>
                    <span className="font-medium ml-2">€{orderData.order.total.toFixed(2)}</span>
                  </div>
                  <div>
                    <span className="text-muted-foreground">{t('deposit_colon')}</span>
                    <span className="font-medium ml-2">€{orderData.order.deposit.toFixed(2)}</span>
                  </div>
                </div>

                {/* Key Dates */}
                <div className="space-y-2">
                  <h4 className="font-medium flex items-center gap-2">
                    <CalendarDays className="w-4 h-4" />
                    {t('important_dates')}
                  </h4>
                  <div className="grid gap-2 text-sm">
                    <div className="flex justify-between">
                      <span className="text-muted-foreground">{t('expected_ready_date_colon')}</span>
                      <span className="font-medium">
                        <time 
                          dateTime={formatDateWithA11y(orderData.order.eta).ariaLabel}
                          title={formatDateWithA11y(orderData.order.eta).ariaLabel}
                        >
                          {formatPeopleDate(orderData.order.eta, { includeTime: true })}
                        </time>
                      </span>
                    </div>
                    {orderData.order.first_fitting_date && (
                      <div className="flex justify-between">
                        <span className="text-muted-foreground">{t('first_fitting_colon')}</span>
                        <span className="font-medium">
                          <time 
                            dateTime={formatDateWithA11y(orderData.order.first_fitting_date).ariaLabel}
                            title={formatDateWithA11y(orderData.order.first_fitting_date).ariaLabel}
                          >
                            {formatPeopleDate(orderData.order.first_fitting_date, { includeTime: true })}
                          </time>
                        </span>
                      </div>
                    )}
                    {orderData.order.second_fitting_date && (
                      <div className="flex justify-between">
                        <span className="text-muted-foreground">{t('second_fitting_colon')}</span>
                        <span className="font-medium">
                          <time 
                            dateTime={formatDateWithA11y(orderData.order.second_fitting_date).ariaLabel}
                            title={formatDateWithA11y(orderData.order.second_fitting_date).ariaLabel}
                          >
                            {formatPeopleDate(orderData.order.second_fitting_date, { includeTime: true })}
                          </time>
                        </span>
                      </div>
                    )}
                    {orderData.order.delivery_date && (
                      <div className="flex justify-between">
                        <span className="text-muted-foreground">{t('delivery_date_colon')}</span>
                        <span className="font-medium">
                          <time 
                            dateTime={formatDateWithA11y(orderData.order.delivery_date).ariaLabel}
                            title={formatDateWithA11y(orderData.order.delivery_date).ariaLabel}
                          >
                            {formatPeopleDate(orderData.order.delivery_date, { includeTime: true })}
                          </time>
                        </span>
                      </div>
                    )}
                   </div>
                </div>

                {/* Payment Overview */}
                {orderData.payment_requests && orderData.payment_requests.length > 0 && (
                  <div className="space-y-2">
                    <h4 className="font-medium flex items-center gap-2">
                      <CreditCard className="w-4 h-4" />
                      {t('payment_overview')}
                    </h4>
                    {(() => {
                      const totalAmount = orderData.payment_requests.reduce((sum, req) => sum + req.amount, 0);
                      const totalPaid = orderData.payment_requests.reduce((sum, req) => sum + (req.paid_amount || 0), 0);
                      const totalRemaining = totalAmount - totalPaid;
                      
                      return (
                        <PaymentProgress 
                          totalAmount={totalAmount}
                          paidAmount={totalPaid} 
                          remainingAmount={totalRemaining}
                        />
                      );
                    })()}
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Invoices */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <CreditCard className="w-5 h-5" />
                  {t('invoices')}
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-3">
                  {orderData.invoices.map((invoice) => {
                    const paidAmount = orderData.payments
                      .filter(p => p.invoice_id === invoice.id)
                      .reduce((sum, p) => sum + p.amount, 0);
                    const isPaid = paidAmount >= invoice.amount;
                    
                    return (
                      <div key={invoice.id} className="flex items-center justify-between p-3 border rounded-lg">
                        <div>
                          <div className="font-medium">{invoice.invoice_number}</div>
                          <div className="text-sm text-muted-foreground">
                            €{invoice.amount.toFixed(2)}
                            {invoice.due_date && (
                              <>
                                {' • '}{t('due')}{' '}
                                <time 
                                  dateTime={formatDateWithA11y(invoice.due_date).ariaLabel}
                                  title={formatDateWithA11y(invoice.due_date).ariaLabel}
                                >
                                  {formatPeopleDate(invoice.due_date, { includeTime: false })}
                                </time>
                              </>
                            )}
                          </div>
                        </div>
                        <div className="flex items-center gap-2">
                          <Badge variant={isPaid ? "default" : "destructive"}>
                            {isPaid ? t('paid_caps') : t('unpaid_caps')}
                          </Badge>
                          {/* No generic pay buttons on invoices - only payment requests */}
                        </div>
                      </div>
                    );
                  })}
                </div>
              </CardContent>
            </Card>

            {/* Payment Requests */}
            {orderData.payment_requests && orderData.payment_requests.length > 0 && (
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <Clock className="w-5 h-5" />
                    {t('portal.requests.title')}
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  {orderData.payment_requests.length === 0 ? (
                    <div className="text-center py-8 text-muted-foreground" role="status" aria-live="polite">
                      <div className="w-16 h-16 mx-auto mb-4 bg-gradient-to-br from-gray-100 to-gray-200 rounded-full flex items-center justify-center">
                        <Clock className="w-8 h-8 text-gray-400" />
                      </div>
                      {t('portal.requests.noneOpen')}
                    </div>
                   ) : (
                     <div className="space-y-3" role="list" aria-label={t('portal.requests.title')}>
                       {(() => {
                         // Find the first unpaid request by due date (already sorted from backend)
                         const unpaidRequests = orderData.payment_requests.filter(req => 
                           req.status !== 'paid' && req.remaining_cents > 0
                         );
                         const firstUnpaidRequest = unpaidRequests.length > 0 ? unpaidRequests[0] : null;
                         
                         return orderData.payment_requests.map((request, index) => {
                           const isOverdue = new Date(request.due_date) < new Date() && request.status !== 'paid';
                           const isPending = request.status === 'pending' || request.status === 'sent' || request.status === 'partially_paid';
                           const paidAmountEuros = request.paid_amount || 0;
                           const remainingAmountEuros = (request.remaining_cents || 0) / 100;
                           const isPayable = firstUnpaidRequest && request.id === firstUnpaidRequest.id;
                           const requestNumber = index + 1;
                           
                           return (
                             <div key={request.id} className={`p-4 border rounded-lg space-y-3 ${isPayable ? 'ring-2 ring-primary/20 bg-primary/5' : ''}`} role="listitem">
                               <div className="flex items-start justify-between">
                                 <div className="flex-1">
                                   <div className="flex items-center gap-2">
                                     <span className={`inline-flex items-center justify-center w-6 h-6 rounded-full text-xs font-medium ${
                                       request.status === 'paid' ? 'bg-green-100 text-green-800' :
                                       isPayable ? 'bg-primary text-primary-foreground' :
                                       'bg-muted text-muted-foreground'
                                     }`}>
                                       {requestNumber}
                                     </span>
                                     <div className="font-medium">€{request.amount.toFixed(2)}</div>
                                   </div>
                                   <div className="text-sm text-muted-foreground ml-8">
                                     {request.description}
                                   </div>
                                   <div className="text-sm text-muted-foreground ml-8">
                                     {t('due_date_colon')}{' '}
                                     <time 
                                       dateTime={formatDateWithA11y(request.due_date).ariaLabel}
                                       title={formatDateWithA11y(request.due_date).ariaLabel}
                                     >
                                       {formatPeopleDate(request.due_date, { includeTime: false })}
                                     </time>
                                     {isOverdue && <span className="text-red-600 ml-2">({t('overdue')})</span>}
                                   </div>
                                 </div>
                                 <div className="flex flex-col sm:flex-row sm:items-center gap-2 sm:gap-3">
                                   <Badge 
                                     variant={
                                       request.status === 'paid' ? 'default' : 
                                       isOverdue ? 'destructive' : 
                                       'secondary'
                                     }
                                   >
                                     {request.status === 'paid' ? t('portal.requests.status.paid') :
                                      request.status === 'partially_paid' ? t('portal.requests.status.partiallyPaid') :
                                      isOverdue ? t('overdue_caps') :
                                      request.status === 'pending' ? t('portal.requests.status.pending') :
                                      request.status.charAt(0).toUpperCase() + request.status.slice(1)}
                                   </Badge>
                                   {isPending && stripeConnected && request.remaining_cents > 0 && (
                                     isPayable ? (
                                       <Button 
                                         size="sm" 
                                         onClick={() => handlePayPaymentRequest(request)}
                                         className="w-full sm:w-auto rounded-md px-4 py-2.5 text-sm font-medium min-h-[44px] sm:min-h-[36px] sm:rounded-full sm:px-3 sm:py-1"
                                       >
                                         {request.status === 'partially_paid' ? t('portal.requests.payRemaining') : t('portal.requests.payNow')}
                                       </Button>
                                     ) : (
                                       <div className="text-xs text-muted-foreground text-center">
                                         <Button 
                                           size="sm" 
                                           disabled
                                           variant="outline"
                                           className="w-full sm:w-auto rounded-md px-4 py-2.5 text-sm font-medium min-h-[44px] sm:min-h-[36px] sm:rounded-full sm:px-3 sm:py-1"
                                         >
                                           {t('portal.requests.payNow')}
                                         </Button>
                                         <div className="mt-1">
                                           {t('portal.requests.completeFirst', {
                                             number: firstUnpaidRequest ? orderData.payment_requests.findIndex(r => r.id === firstUnpaidRequest.id) + 1 : 1,
                                           })}
                                         </div>
                                       </div>
                                     )
                                   )}
                                 </div>
                               </div>
                               
                               {/* Payment Progress - Show for all requests */}
                               <PaymentProgress
                                 totalAmount={request.amount}
                                 paidAmount={paidAmountEuros}
                                 remainingAmount={remainingAmountEuros}
                               />
                             </div>
                           );
                         });
                       })()}
                       </div>
                  )}
                </CardContent>
              </Card>
            )}

            {/* Payment History */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <CreditCard className="w-5 h-5" />
                  {t('payment_history')}
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-3">
                  {orderData.payments.length === 0 ? (
                    <p className="text-sm text-muted-foreground">{t('no_payments_yet')}</p>
                  ) : (
                    orderData.payments
                      .sort((a, b) => {
                        const dateA = new Date(a.paid_at || a.date);
                        const dateB = new Date(b.paid_at || b.date);
                        return dateB.getTime() - dateA.getTime(); // newest first
                      })
                      .map((payment) => (
                      <div key={payment.id} className="flex items-center justify-between p-3 border rounded-lg">
                        <div>
                          <div className="font-medium">€{payment.amount.toFixed(2)}</div>
                           <div className="text-sm text-muted-foreground">
                             {payment.method === 'cash' ? t('cash_caps') : payment.method} • {' '}
                             <time 
                               dateTime={formatDateWithA11y(payment.paid_at || payment.date).ariaLabel}
                               title={formatDateWithA11y(payment.paid_at || payment.date).ariaLabel}
                             >
                               {formatPaymentTime(payment, { relative: true })}
                             </time>
                           </div>
                        </div>
                        <Badge variant="outline">{t('completed_caps')}</Badge>
                      </div>
                    ))
                  )}
                </div>
              </CardContent>
            </Card>

            {/* Timeline */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Clock className="w-5 h-5" />
                  {t('order_timeline')}
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  {orderData.timeline.map((event, index) => (
                    <div key={index} className="flex gap-3">
                      <div className="w-2 h-2 bg-primary rounded-full mt-2 flex-shrink-0" />
                       <div>
                         <div className="font-medium">
                           {event.description === 'Order placed' ? t('order_placed_caps') : 
                            event.description === 'Deposit received' ? t('deposit_received_caps') : 
                            event.description}
                         </div>
                          <div className="text-sm text-muted-foreground">
                            <time 
                              dateTime={formatDateWithA11y(event.ts).ariaLabel}
                              title={formatDateWithA11y(event.ts).ariaLabel}
                            >
                              {formatPeopleDate(event.ts, { relative: true, includeTime: true })}
                            </time>
                          </div>
                       </div>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          </>
        )}

        {/* Payment Request Modal */}
        <PaymentRequestModal
          open={paymentRequestModalOpen}
          onOpenChange={setPaymentRequestModalOpen}
          paymentRequest={selectedPaymentRequest}
          orderNumber={orderData?.order?.id || ''}
          customerEmail={customer?.email || ''}
          organizationMinCents={orgMinPaymentCents}
          onSuccess={() => {
            setPaymentRequestModalOpen(false);
            setSelectedPaymentRequest(null);
            toast({
              title: t('portal.pay.success'),
              description: t('payment_processed_successfully'),
            });
            // Refresh order data to get updated payment requests and payment history
            if (selectedOrderId) {
              fetchOrderData(selectedOrderId);
            }
          }}
        />
      </div>
    </ErrorBoundary>
  );
}
